import { Button, FormGroup, Icon, InputGroup } from "@blueprintjs/core";
import React, { useEffect, useState } from "react";
import { ColorChangeHandler, SketchPicker, TwitterPicker } from "react-color";
import { ErrorBoundary } from "react-error-boundary";

import { useAppSelector } from "../../redux/hooks";
import {
  Tag,
  useCreateTagMutation,
  useEditTagMutation,
  useGetUserPlaySettingsQuery,
  useUpdateUserPlaySettingsMutation,
} from "../../redux/slices";
import { SportPlay } from "../../types";
import { EditPlayProps } from "../edit-play";
import { Modal } from "../modal/modal";
import styles from "./tag-creator.module.css";

export interface TagCreatorProps<TPlay extends SportPlay> extends Partial<Pick<EditPlayProps<TPlay>, "updatePlay">> {
  show: boolean;
  handleClose: () => void;
  tag?: Tag;
}

export const TagCreator = <TPlay extends SportPlay>({ show, handleClose, tag, updatePlay }: TagCreatorProps<TPlay>) => {
  const { data: userPlaySettings } = useGetUserPlaySettingsQuery();
  const [updateUserPlaySettings] = useUpdateUserPlaySettingsMutation();
  const [createTag] = useCreateTagMutation();
  const theme = useAppSelector((state) => state.theme);
  const [editTag] = useEditTagMutation();

  const [tagName, setTagName] = useState<string>(tag ? tag.name : "");
  const [textColor, setTextColor] = useState<string>(tag ? tag.textColor : "#FFFFFF");
  const [backgroundColor, setBackgroundColor] = useState<string>(tag ? tag.color : "#000000");

  const textColors = ["#B8B8B8", "#4A4A4A", "#FFFFFF", "#000000", `#${theme.teamColor}`];

  useEffect(() => {
    if (!tag) setBackgroundColor(`#${theme.teamColor}`);
  }, [tag, theme.teamColor]);

  const handleBackgroundColorChange: ColorChangeHandler = (color) => {
    setBackgroundColor(color.hex);
  };

  const handleTextColorChange: ColorChangeHandler = (color) => {
    setTextColor(color.hex);
  };

  const handleCreateTagClick = async () => {
    let newTag: Tag;
    if (tag) {
      newTag = await (
        await editTag({ id: tag.id, name: tagName, color: backgroundColor, textColor, tag_id: tag.tag_id }).unwrap()
      ).data;
    } else {
      newTag = await (await createTag({ name: tagName, color: backgroundColor, textColor }).unwrap()).data;
    }

    if (userPlaySettings) {
      // if color is not in userPlaySettings.colors, add it
      if (!userPlaySettings.colors || !userPlaySettings.colors.includes(newTag.color)) {
        updateUserPlaySettings({
          ...userPlaySettings,
          colors: userPlaySettings.colors ? [...userPlaySettings.colors, newTag.color] : [newTag.color],
        });
      }
    }

    if (updatePlay) {
      updatePlay((prevState) => ({ ...prevState, tags: [...prevState.tags, newTag] }));
    }

    handleCloseClick();
  };

  const handleCloseClick = () => {
    setTagName("");
    setBackgroundColor(`#${theme.teamColor}`);
    setTextColor("#FFFFFF");
    handleClose();
  };

  const handleRemoveColor = (color: string) => {
    if (userPlaySettings) {
      updateUserPlaySettings({
        ...userPlaySettings,
        colors: userPlaySettings.colors.filter((c) => c !== color),
      });
    }
  };

  return (
    <Modal
      title={tag ? "Edit Tag" : "Create Tag"}
      isOpen={show}
      onClose={handleCloseClick}
      actions={[
        <Button intent="danger" text="Cancel" onClick={handleCloseClick} minimal={true} key={1} />,
        <Button
          intent="primary"
          text={tag ? "Save" : "Create"}
          onClick={handleCreateTagClick}
          disabled={tagName.length === 0}
          key={2}
        />,
      ]}
    >
      <div className={styles.tagPreviewContainer}>
        <TagPreview backgroundColor={backgroundColor} textColor={textColor} tagName={tagName || "New Tag"} />
      </div>
      <FormGroup label="Tag Name" labelFor="tag-name" labelInfo="(required)">
        <InputGroup id="tag-name" placeholder="Tag Name" value={tagName} onChange={(e) => setTagName(e.target.value)} />
      </FormGroup>
      <FormGroup label="Background Color" labelFor="background-color" labelInfo="(required)">
        <div className={styles.colorOptionContainer}>
          <div>
            <ErrorBoundary fallback={<div>Something went wrong</div>}>
              <SketchPicker
                color={backgroundColor}
                onChange={handleBackgroundColorChange}
                disableAlpha={true}
                presetColors={[]}
              />
            </ErrorBoundary>
          </div>
          <div className={styles.savedColorContainer}>
            <div className={styles.colorContainer}>
              {userPlaySettings?.colors &&
                userPlaySettings.colors.map((color, index) => (
                  <ColorButton
                    color={color}
                    onClick={() => setBackgroundColor(color)}
                    key={index}
                    onRemoveClick={() => handleRemoveColor(color)}
                  />
                ))}
            </div>
          </div>
        </div>
      </FormGroup>
      <FormGroup label="Text Color" labelFor="text-color" labelInfo="(required)">
        <div className={styles.colorOptionContainer}>
          <div>
            <ErrorBoundary fallback={<div>Something went wrong</div>}>
              <TwitterPicker color={textColor} colors={textColors} triangle="hide" onChange={handleTextColorChange} />
            </ErrorBoundary>
          </div>
        </div>
      </FormGroup>
    </Modal>
  );
};

interface ColorButtonProps {
  color: string;
  onClick: () => void;
  onRemoveClick: () => void;
}

const ColorButton = ({ color, onClick, onRemoveClick }: ColorButtonProps) => {
  const [showRemove, setShowRemove] = useState<boolean>(false);

  return (
    <div
      className={styles.colorButton}
      style={{ backgroundColor: color }}
      onClick={onClick}
      onMouseEnter={() => setShowRemove(true)}
      onMouseLeave={() => setShowRemove(false)}
    >
      {showRemove && (
        <Icon icon="cross" iconSize={12} color="#fff" className={styles.removeColor} onClick={onRemoveClick} />
      )}
    </div>
  );
};

// to be replaced with Tag component
export const TagPreview = ({
  backgroundColor,
  textColor,
  tagName,
}: {
  backgroundColor: string;
  textColor: string;
  tagName: string;
}) => {
  return (
    <div className={styles.tagPreview} style={{ backgroundColor, color: textColor }}>
      {tagName}
    </div>
  );
};
